import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateEffect } from "react-use";
import { Body2, Select, SelectTrigger } from "@activecollab/components";
import moment from "moment";
import PropTypes from "prop-types";
import useAcSelectDate from "../Hooks/useAcSelectDate";
import SelectDateNonWorkingDays from "../SelectDate/SelectDateNonWorkingDays";

export const CalendarEventRepeatProperty = ({ repeatEvent, repeatUntil, canEdit, disableDaysBefore, onChange }) => {
  const { t } = useTranslation();

  const repeatOptions = useMemo(
    () => [
      {
        id: "dont",
        name: t("Never"),
      },
      {
        id: "daily",
        name: t("Daily"),
      },
      {
        id: "weekly",
        name: t("Weekly"),
      },
      {
        id: "biweekly",
        name: t("Biweekly"),
      },
      {
        id: "monthly",
        name: t("Monthly"),
      },
      {
        id: "yearly",
        name: t("Yearly"),
      },
    ],
    [t]
  );

  const selected = useMemo(() => {
    return {
      repeatEvent: repeatOptions.find((option) => option.id === repeatEvent),
      repeatUntil: typeof repeatUntil === "number" ? repeatUntil : null,
    };
  }, [repeatEvent, repeatOptions, repeatUntil]);

  const { dates, handleDateChange } = useAcSelectDate({ from: selected.repeatUntil, to: selected.repeatUntil });

  const handleRepeatChange = useCallback(
    (value) => {
      if (typeof onChange === "function") {
        onChange({
          repeat_event: repeatOptions.find((option) => option.id === value)?.id,
        });
      }
    },
    [onChange, repeatOptions]
  );

  const handleRepeatUntilChange = useCallback(
    (dates) => {
      if (dates) {
        handleDateChange(dates);
        if (typeof onChange === "function") {
          onChange({
            repeat_until: moment.unix(dates.from).format("YYYY-MM-DD"),
          });
        }
      } else {
        handleDateChange({ from: null, to: null });
        if (typeof onChange === "function") {
          onChange({
            repeat_until: null,
          });
        }
      }
    },
    [handleDateChange, onChange]
  );

  useUpdateEffect(() => {
    handleDateChange({ from: selected.repeatUntil, to: selected.repeatUntil });
  }, [handleDateChange, selected.repeatUntil]);

  return (
    <div className="tw-flex tw-gap-4 tw-flex-wrap tw-justify-center">
      <div className="tw-h-[62px] tw-flex tw-flex-col tw-justify-between">
        <Body2 weight="bold">{t("Repeats")}</Body2>
        <Select
          target={
            <SelectTrigger
              data-testid="repeat-trigger"
              size="big"
              className="tw-w-36"
              disabled={!canEdit}
            >
              {selected.repeatEvent ? selected.repeatEvent.name : t("Never")}
            </SelectTrigger>
          }
          selected={selected.repeatEvent?.id}
          options={repeatOptions}
          onChange={handleRepeatChange}
          disableSearch
          forceCloseMenu
          disabledInternalSort
          keepSameOptionsOrder
        />
      </div>
      {selected.repeatEvent?.id !== "dont" ? (
        <div className="tw-h-[62px] tw-flex tw-flex-col tw-justify-between">
          <Body2 weight="bold">{t("Until")}</Body2>
          <SelectDateNonWorkingDays
            trigger={(text) => (
              <SelectTrigger
                data-testid="repeat-until-trigger"
                size="big"
                className="tw-w-36"
                disabled={!canEdit}
              >
                {selected.repeatUntil ? text : `${t("Forever")}`}
              </SelectTrigger>
            )}
            selectionMode="daily"
            changeMode="atomic"
            onSave={handleRepeatUntilChange}
            selectedDays={dates.from ? dates : undefined}
            disableDaysBefore={moment.unix(disableDaysBefore)}
            weekendIsSelectable
            type="selectable_global_day_off"
          />
        </div>
      ) : null}
    </div>
  );
};

CalendarEventRepeatProperty.propTypes = {
  repeatEvent: PropTypes.string,
  repeatUntil: PropTypes.number,
  canEdit: PropTypes.bool,
  disableDaysBefore: PropTypes.number,
  onChange: PropTypes.func,
};
